import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import { useQuery } from 'react-query';
import { CustomTooltip } from 'src/components/CustomTooltip';
import SubtitleLink from 'src/components/SubtitleLink';
import { getLatestSecondaryMarketNegotiations } from 'src/services/operation';
import { convertValue, toCurrency } from 'src/utils/number';

interface LatestNegotiation {
  negotiation_id: string;
  market?: string | null;
  asset_backing?: string | null;
  negotiated_amount?: number | string | null;
  negotiation_date?: string | null;
}

interface LatestNegotiationsResponse {
  data: LatestNegotiation[];
}

const headers = ['Mercado', 'Ativo-lastro', 'Montante Negociado'];

const formatValue = (value?: string | null): string =>
  value && value !== '-' ? value : '—';

const formatDate = (value?: string | null): string => {
  if (!value || value === '-') return '—';

  try {
    return format(parseISO(value.split('T')[0]), 'dd/MM/yyyy');
  } catch {
    return '—';
  }
};

const toNumericValue = (value?: number | string | null): number | null => {
  if (value === null || value === undefined || value === '') return null;

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const formatCompactCurrency = (value?: number | string | null): string => {
  const numericValue = toNumericValue(value);
  if (numericValue === null) return '—';

  return convertValue(numericValue, '', true, 'currency', 'BRL', 1, 0);
};

const formatCurrency = (value?: number | string | null): string => {
  const numericValue = toNumericValue(value);
  return numericValue === null ? '—' : toCurrency(numericValue);
};

export default function LatestNegotiations() {
  const {
    data: negotiations,
    isError,
    isLoading,
  } = useQuery<LatestNegotiationsResponse>(
    'latestSecondaryMarketNegotiationsHome',
    getLatestSecondaryMarketNegotiations,
    { retry: false },
  );
  const rows = negotiations?.data ?? [];

  return (
    <Paper elevation={0} sx={{ p: '16px 16px 8px 24px' }}>
      <SubtitleLink
        title="Negociações recentes"
        url="/operacoes?market=FIDC&mode=advanced&indicator=fidc-secundario"
        labelLink="Ver todas as negociações"
      />
      <Table
        size="small"
        aria-label="Negociações recentes"
        sx={{ mt: 2, tableLayout: 'fixed', width: '100%' }}>
        <TableHead>
          <TableRow>
            {headers.map((header) => (
              <TableCell
                key={header}
                sx={{
                  color: '#797979',
                  fontSize: '14px',
                  fontWeight: 600,
                  overflowWrap: 'anywhere',
                  px: 0.5,
                  py: 0.75,
                  whiteSpace: 'normal',
                }}>
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading && (
            <TableRow>
              <TableCell
                colSpan={headers.length}
                sx={{ px: 1, py: 3, textAlign: 'center' }}>
                <CircularProgress
                  size={20}
                  aria-label="Carregando negociações"
                />
              </TableCell>
            </TableRow>
          )}
          {isError && (
            <TableRow>
              <TableCell
                colSpan={headers.length}
                sx={{ px: 1, py: 3, textAlign: 'center' }}>
                Não foi possível carregar as negociações.
              </TableCell>
            </TableRow>
          )}
          {!isLoading && !isError && rows.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={headers.length}
                sx={{ px: 1, py: 3, textAlign: 'center' }}>
                Nenhuma negociação encontrada.
              </TableCell>
            </TableRow>
          )}
          {rows.map((negotiation) => (
            <TableRow key={negotiation.negotiation_id}>
              <TableCell
                sx={{
                  fontSize: '14px',
                  overflowWrap: 'anywhere',
                  px: 0.5,
                  py: 1,
                }}>
                {formatValue(negotiation.market)}
              </TableCell>
              <TableCell
                sx={{
                  fontSize: '14px',
                  overflowWrap: 'anywhere',
                  px: 0.5,
                  py: 1,
                }}>
                {formatValue(negotiation.asset_backing)}
              </TableCell>
              <TableCell
                sx={{
                  fontSize: '14px',
                  overflowWrap: 'anywhere',
                  px: 0.5,
                  py: 1,
                }}>
                <CustomTooltip
                  title={formatCurrency(negotiation.negotiated_amount)}>
                  <Box component="span">
                    {formatCompactCurrency(negotiation.negotiated_amount)}
                  </Box>
                </CustomTooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}
